import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { setMessage } from "./message";
import AuthService from "../services/auth.service";
import { isTokenExpired } from "../helpers";
import { AuthState } from "../interfaces/types";

// ✅ El tipo real del user, tomado de tu AuthState en types.ts
type AuthUser = NonNullable<AuthState["user"]>;

interface RegisterPayload {
  username: string;
  email: string;
  password: string;
}

interface LoginPayload {
  username: string;
  password: string;
}

interface ForgotPasswordPayload {
  username: string;
}

interface ForgotPasswordUserPayload {
  username: string;
}

interface ResetPasswordPayload {
  token: string;
  email: string;
  password: string;
}

interface ResetPasswordUserPayload {
  token: string;
  username: string;
  password: string;
}

interface GetResetPasswordPayload {
  token: string;
}

// ✅ localstorage user
const userFromStorage: AuthUser | null = (() => {
  try {
    const stored = JSON.parse(localStorage.getItem("user") || "null");
    if (!stored || !Array.isArray(stored.roles) || isTokenExpired(stored)) {
      localStorage.removeItem("user");
      return null;
    }
    return stored;
  } catch {
    return null;
  }
})();

export const register = createAsyncThunk(
  "auth/register",
  async (payload: RegisterPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.register(payload.username, payload.email, payload.password);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async (payload: LoginPayload, thunkAPI: any) => {
    try {
      // ⚠️ tu AuthService.login retorna AxiosResponse
      const response = await AuthService.login(payload.username, payload.password);
      const data = response.data as AuthUser; // ✅ el mismo shape que guardás en localStorage
      return { user: data };
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "auth/forgot-password",
  async (payload: ForgotPasswordPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.forgotPassword(payload.username);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const forgotPasswordUser = createAsyncThunk(
  "auth/forgot-password-user",
  async (payload: ForgotPasswordUserPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.forgotPasswordUser(payload.username);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/reset-password",
  async (payload: ResetPasswordPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.resetPassword(payload.token, payload.email, payload.password);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const resetPasswordClient = createAsyncThunk(
  "auth/reset-password-client",
  async (payload: ResetPasswordPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.resetPasswordClient(payload.token, payload.email, payload.password);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const resetPasswordUser = createAsyncThunk(
  "auth/reset-password-user",
  async (payload: ResetPasswordUserPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.resetPasswordUser(payload.token, payload.username, payload.password);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const resetClientPassword = createAsyncThunk(
  "auth/reset-client-password",
  async (payload: ResetPasswordUserPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.resetClientPassword(payload.token, payload.username, payload.password);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getResetPassword = createAsyncThunk(
  "auth/get-reset-password",
  async (payload: GetResetPasswordPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.getResetPassword(payload.token);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getResetPasswordClient = createAsyncThunk(
  "auth/get-reset-password-client",
  async (payload: GetResetPasswordPayload, thunkAPI: any) => {
    try {
      const response = await AuthService.getResetPasswordClient(payload.token);
      thunkAPI.dispatch(setMessage(response.data.message));
      return response.data;
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage(message));
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const logout = createAsyncThunk("auth/logout", async () => {
  await AuthService.logout(); // esto ya borra localStorage en tu AuthService.logout()
});

const initialState: AuthState & { isLoggedIn: boolean } = userFromStorage
  ? { isLoggedIn: true, user: userFromStorage }
  : { isLoggedIn: false, user: null };

const authSlice = createSlice({
  name: "auth",
  initialState,

  reducers: {
    // ✅ Actualiza conditions en Redux + localStorage (solo si existe en el user)
    setUserConditions: (state, action: PayloadAction<boolean>) => {
      if (!state.user) return;

      // si no viene "conditions" en el JWT => users/admin => ignorar
      if (!Object.prototype.hasOwnProperty.call(state.user, "conditions")) return;

      state.user = { ...state.user, conditions: action.payload };

      try {
        const raw = localStorage.getItem("user");
        if (!raw) return;
        const parsed = JSON.parse(raw);
        parsed.conditions = action.payload;
        localStorage.setItem("user", JSON.stringify(parsed));
      } catch (e) {
        console.error("Error updating localStorage user.conditions", e);
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => { state.isLoggedIn = false; state.user = null; })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoggedIn = true;
        state.user = action.payload.user;
      })
      .addCase(login.rejected, (state) => {
        state.isLoggedIn = false;
        state.user = null;
      })

      // logout
      .addCase(logout.fulfilled, (state) => {
        state.isLoggedIn = false;
        state.user = null;
      })
      .addCase(logout.rejected, (state) => {
        state.isLoggedIn = false;
        state.user = null;
      });
  },
});

export const { setUserConditions } = authSlice.actions;
export default authSlice.reducer;
